import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import Reveal from "@/components/Reveal";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  return (
    <div
      data-testid="chooser-page"
      className="relative min-h-screen flex items-center overflow-hidden grain pt-32 pb-20"
    >
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-br from-volt/5 via-pitch to-sky-500/5"
        aria-hidden="true"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="text-center">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-xs font-mono uppercase tracking-[0.3em] text-mint"
            data-testid="chooser-eyebrow"
          >
            Welcome — choose your app
          </motion.p>
          <h1
            className="mt-5 font-display leading-[0.9] tracking-tight text-[clamp(3.5rem,9vw,7.5rem)]"
            data-testid="chooser-headline"
          >
            <Line delay={0.25}>WHAT ARE WE</Line>
            <Line delay={0.4}>
              <span className="text-outline">PLAYING TODAY?</span>
            </Line>
          </h1>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-8">
          <Reveal delay={0.55}>
            <Link to="/flappy-goal" data-testid="chooser-card-flappy-goal" className="block group">
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="relative h-full overflow-hidden rounded-3xl border border-line bg-turf/70 p-10 sm:p-12 transition-colors duration-300 group-hover:border-volt/60"
              >
                <div
                  className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-volt/10 blur-3xl"
                  aria-hidden="true"
                />
                <LogoMark size={64} />
                <h2 className="mt-8 font-display text-5xl sm:text-6xl tracking-tight leading-none">
                  FLAPPY <span className="text-volt">GOAL</span>
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-sm">
                  The flappy arcade soccer game. One thumb, flying goal posts,
                  endless glory.
                </p>
                <span className="mt-8 inline-flex items-center gap-2.5 font-bold text-volt">
                  Enter the pitch
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform duration-200 group-hover:translate-x-1.5"
                  />
                </span>
              </motion.div>
            </Link>
          </Reveal>

          <Reveal delay={0.7}>
            <Link to="/court-vision" data-testid="chooser-card-court-vision" className="block group">
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="relative h-full overflow-hidden rounded-3xl border border-line bg-turf/70 p-10 sm:p-12 transition-colors duration-300 group-hover:border-sky-400/60"
              >
                <div
                  className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-sky-400/10 blur-3xl"
                  aria-hidden="true"
                />
                <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
                  <rect x="6" y="4" width="52" height="56" rx="8" fill="#14532D" />
                  <rect x="14" y="10" width="36" height="44" fill="none" stroke="#F8FAFC" strokeWidth="2.5" />
                  <line x1="10" y1="32" x2="54" y2="32" stroke="#F8FAFC" strokeWidth="3" />
                  <circle cx="46" cy="18" r="5" fill="#D9F99D" stroke="#84CC16" strokeWidth="1.5" />
                </svg>
                <h2 className="mt-8 font-display text-5xl sm:text-6xl tracking-tight leading-none">
                  COURT <span className="text-sky-400">VISION</span>
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-sm">
                  Automatic tennis line calling from your phone. Every ball
                  called IN or OUT — no more arguments.
                </p>
                <span className="mt-8 inline-flex items-center gap-2.5 font-bold text-sky-400">
                  Enter the court
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform duration-200 group-hover:translate-x-1.5"
                  />
                </span>
              </motion.div>
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
